import { useEffect, useState, useMemo } from "react";
import styles from "./PromotionalCountdownTimer.module.scss";
import { monthNumberMap } from "../promotionalMessagesUtils";

type Props = {
  message: string;
  availableToOrder: boolean;
};

type Time = {
  hours: number;
  minutes: number;
  seconds: number;
};

const regexSavePoundsUntil = /Save £\d+\.?\d* until \.?/;
const regexSavePoundsOnSelectedUntil =
  /Save £\d+\.?\d* on selected products until \.?/;
const regexSavePercentageUntil = /Save \d+\.?\d*% until \.?/;
const regexSavePercentageOnSelectedUntil =
  /Save \d+\.?\d*% on selected products until \.?/;

const shouldRenderCountdown = (
  hours: number,
  minutes: number,
  seconds: number,
  message: string,
  availableToOrder: boolean,
) => {
  if (
    !regexSavePoundsUntil.test(message) &&
    !regexSavePoundsOnSelectedUntil.test(message) &&
    !regexSavePercentageUntil.test(message) &&
    !regexSavePercentageOnSelectedUntil.test(message)
  ) {
    return false;
  }

  if (!availableToOrder) {
    return false;
  }

  // if all of the values are 0 the end date is now
  if (hours === 0 && minutes === 0 && seconds === 0) {
    return false;
  }

  //only show timer within 24 hours of promo end date
  if (hours >= 24) {
    return false;
  }

  return true;
};

const calculateTimeLeft = (countDownDate: Date): number => {
  const targetTime = countDownDate.getTime();
  const currentTime = Date.now();
  const difference = (targetTime - currentTime) / 1000;
  return difference < 0 ? 0 : difference;
};

const formatTimer = (hours: number, minutes: number, seconds: number) => {
  let countdownString = "";

  if (hours === 0 && minutes !== 0) {
    countdownString = `${minutes}m ${seconds}s`;
  } else if (hours === 0 && minutes === 0) {
    countdownString = `${seconds}s`;
  } else {
    countdownString = `${hours}h ${minutes}m ${seconds}s`;
  }
  return countdownString;
};

export const PromotionalCountdownTimer = ({
  message,
  availableToOrder,
}: Props) => {
  const [isMounted, setIsMounted] = useState(false);
  const regexCountDownDate =
    /until\s(0?[1-9]|[1-2][0-9]|3[01])\s(January|February|March|April|May|June|July|August|September|October|November|December)\s(\d{4})/;
  const extractedCountDownDate = message.match(regexCountDownDate);

  const [, day, monthName, year] = extractedCountDownDate ?? []; //first element is skipped as it represents the entire matched string
  const month = monthName ? monthNumberMap[monthName] : undefined;

  const countDownDate = useMemo(() => {
    if (year && month && day)
      return new Date(Number(year), month - 1, Number(day), 23, 45, 0, 0); // add additional time to take end to 23:45:00 on end date - when promotions end | months are 0-based
    else return null;
  }, []);
  const [timeLeft, setTimeLeft] = useState<number>(
    countDownDate ? calculateTimeLeft(countDownDate) : 0,
  );

  useEffect(() => {
    setIsMounted(true); //fixes react hydration issue
  }, []);

  useEffect(() => {
    let intervalSeconds: NodeJS.Timeout;
    if (timeLeft > 0) {
      intervalSeconds = setInterval(() => {
        setTimeLeft((number) => number - 1);
      }, 1000);
    }
    return () => {
      clearInterval(intervalSeconds);
    };
  }, [timeLeft]);

  if (!isMounted) {
    return null;
  }

  if (!countDownDate) {
    return null;
  }

  const formatTimeDifference = (timeLeft: number): Time => {
    const hours = Math.floor(timeLeft / 3600);
    const minutes = Math.floor((timeLeft % 3600) / 60);
    const seconds = Math.floor(timeLeft % 60);
    return { hours, minutes, seconds };
  };

  const { hours, minutes, seconds } = formatTimeDifference(timeLeft);

  if (
    !shouldRenderCountdown(hours, minutes, seconds, message, availableToOrder)
  ) {
    return null;
  }

  return (
    <div
      className={styles.promoCountdownBadge}
      data-testid="product:promotional-countdown-timer"
    >
      {`Ends in ${formatTimer(hours, minutes, seconds)}`}
    </div>
  );
};
